import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, Grip, Palette, PinOff } from 'lucide-react';
import type { RailItem, RailUtilitySlots } from '@clarix/shared-types';
import type { PanelTitles } from './SidebarPanel';
import { SidebarHoverPanel } from './SidebarHoverPanel';
import { SidebarThemePanel } from './SidebarThemePanel';
import { useSidebarTheme } from './hooks/useSidebarTheme';
import { deriveSidebarTheme, isDarkColor } from './theme/sidebarTheme';
import { derivePalette, DEFAULT_PALETTE, PALETTE_SHADES } from './theme/palette';
import {
  Avatar,
  AvatarFallback,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@clarix/ui';
import { useAuthStore } from '@clarix/auth-store';
import { useSidebar } from './context/SidebarContext';
import { useSidebarRailPins } from './hooks/useSidebarRailPins';
import { SidebarMorePopover, type SidebarMoreItem } from './SidebarMorePopover';
import { UserMenu } from './UserMenu';
import { useRailBadge } from './rail-badges';

/** Past this the pill reads "99+"; the aria-label keeps the real number. Badges show on primary items only. */
const BADGE_CAP = 99;

interface SidebarRailProps {
  /** Items shown above the More popover. Aggregated by the shell as
   *  `[...BASE_RAIL_ITEMS.top, ...activeApp.railItems]`. */
  items: readonly RailItem[];
  /** Items shown inside the More popover. */
  moreItems?: readonly RailItem[];
  /** Click handler for the Tracker bottom button. Optional; stays hidden
   *  if not provided. */
  onOpenTrackerModal?: () => void;
  /** App switcher, rendered at the bottom of the rail (above the user
   *  avatar). The shell composes it from APP_REGISTRY + the user's app
   *  access; the rail just renders the slot. */
  appSwitcher?: ReactNode;
  /** Supplies the body of the hover/pinned panel for a panelId (from the shell). */
  renderPanelContent?: (panelId: string, onNavigate: () => void) => ReactNode;
  /** Optional per-item navigation-target resolver. Lets the shell override
   *  where a rail item navigates on click based on runtime data (e.g. the
   *  Projects item routing a non-admin to their first project instead of the
   *  list page). Falls back to `item.href` when not provided or returns
   *  undefined. Does not affect active-state matching, which still uses
   *  `href` / `matchPaths`. */
  resolveHref?: (item: RailItem) => string | undefined;
  /**
   * Render the app's nav as present-but-inert (dimmed, not clickable, out of the
   * tab order). Used when the active app's backend is down: the items are shown
   * so the sidebar still looks like that app, but nothing in it can be reached.
   *
   * Scoped to the `<nav>` ONLY. The app switcher, Tracker button and user menu
   * below it stay fully interactive — during an outage those are the user's way
   * OUT, so disabling them would trap them on a dead app.
   */
  disabled?: boolean;
  /** Header titles declared by the active app's sidebar config. */
  panelTitles?: PanelTitles;
  /**
   * Which bottom utility slots the active app shows. Omitted fields render, so
   * an app opts OUT; the user menu is never configurable (it is the only route
   * to Sign Out).
   */
  utility?: RailUtilitySlots;
  /** The active app's rail is not resolved yet: hold the nav empty until its items are known. */
  loading?: boolean;
}

/** First-open delay for the hover panel, so sweeping the pointer across the rail opens nothing. */
export const HOVER_OPEN_DELAY_MS = 300;

/**
 * Is this element's text clipped by its own box (i.e. not fully readable)?
 *
 * Measures BOTH axes because the caption wraps: a label longer than the
 * two-line clamp overflows vertically (scrollHeight), while an unbreakable
 * word wider than the rail overflows horizontally (scrollWidth). Checking
 * width alone would miss every wrapped label.
 *
 * Re-measures on element resize AND after web fonts finish loading — a font
 * swap changes the text's width without changing the element's box, so a
 * ResizeObserver alone never fires and the label would be judged on the
 * fallback font's metrics.
 */
function useIsTextClipped(ref: React.RefObject<HTMLElement | null>, text: string): boolean {
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // +1px tolerance: sub-pixel layout can report a 1px overflow on text that
    // visually fits, which would attach a tooltip showing the same string.
    const measure = () =>
      setClipped(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);
    measure();

    let observer: ResizeObserver | undefined;
    // jsdom (and older browsers) have no ResizeObserver — the one-shot measure
    // above still runs, we just don't re-measure on resize.
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }

    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) measure();
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [ref, text]);

  return clipped;
}

/**
 * One rail item: its icon button and its caption.
 *
 * Every label renders at ONE type size across every app — no per-item
 * step-down for long words, which made the rail look ragged. The rail is a
 * fixed 64px, so a label that doesn't fit on one line WRAPS onto a second
 * ("Variable Pay" → "Variable" / "Pay") rather than being cut to "Variable…".
 * Wrapping prefers word boundaries and only breaks inside a word when a single
 * word can't fit at all; past two lines the clamp ellipsises and the tooltip
 * carries the full text.
 *
 * The tooltip wraps the WHOLE item, not just the caption. The caption is a ~10px
 * strip under a 36px icon button, so a trigger scoped to the text alone almost
 * never fires — people aim at the icon. It is attached only when the text is
 * ACTUALLY clipped (measured, not guessed from length — the cut-off depends on
 * the rendered glyph widths), and never for an item with a `panelId`: those open
 * their sub-sidebar on the same hover, whose header already spells the name out
 * in full, and a tooltip would render on top of that panel.
 */
function RailItemView({
  item,
  active,
  dark,
  onClick,
  onHoverEnter,
  onHoverLeave,
}: {
  item: RailItem;
  active: boolean;
  dark: boolean;
  onClick: () => void;
  onHoverEnter: (anchor: HTMLElement) => void;
  onHoverLeave: () => void;
}) {
  const labelRef = useRef<HTMLSpanElement>(null);
  // The rail shows the abbreviation when the item declares one; `item.label`
  // stays the real name for the accessible name and the tooltip.
  const caption = item.shortLabel ?? item.label;
  const clipped = useIsTextClipped(labelRef, caption);
  const Icon = item.icon;
  const color = active ? 'var(--sr-active-icon)' : 'var(--sr-icon)';
  const opacity = active ? 1 : dark ? 0.65 : 0.6;
  const badge = useRailBadge(item.badgeKey);
  const badgeId = useId();

  const body = (
    <div
      className="flex w-full flex-col items-center gap-0.5"
      onMouseEnter={(e) => onHoverEnter(e.currentTarget)}
      onMouseLeave={onHoverLeave}
    >
      <div className="relative">
        <button
          type="button"
          onClick={onClick}
          aria-label={item.label}
          aria-describedby={badge ? badgeId : undefined}
          className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors"
          style={{ background: active ? 'var(--sr-active)' : 'transparent', color, opacity }}
        >
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </button>
        {/* A sibling of the button, not a child, so it stays at full opacity on an idle item. */}
        {badge ? (
          <span
            id={badgeId}
            role="img"
            aria-label={`${badge} ${item.badgeLabel ?? 'pending'}`}
            className="pointer-events-none absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-xs font-bold leading-none text-destructive-foreground"
          >
            {badge > BADGE_CAP ? `${BADGE_CAP}+` : badge}
          </span>
        ) : null}
      </div>
      {/* The nav's px-1 leaves a 55px line (64px rail, 1px border). "Attendance"
          measures ~56.5px at text-xs, so its last letter wraps alone. 4px wider
          keeps that word on one line and still 2px off the rail edge. A spaced
          label such as "Variable Pay" still wraps at the space; line-clamp-2
          stops a caption past two lines. */}
      <span
        ref={labelRef}
        className="pointer-events-none -mx-0.5 line-clamp-2 w-[calc(100%+4px)] select-none break-words text-center text-xs font-medium leading-tight"
        style={{ color, opacity }}
      >
        {caption}
      </span>
    </div>
  );

  // Disclose the full name when the rail isn't showing it — either because the
  // item declares an abbreviation, or because the caption is clipped. Panel
  // items are excluded: their sub-sidebar opens on the same hover and its header
  // already spells the name out, so a tooltip would land on top of that panel.
  const needsFullName = item.shortLabel !== undefined || clipped;
  if (!needsFullName || item.panelId) return body;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{body}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Sidebar rail — 64px wide, fixed left, full height. Exact-copy chrome
 * from clario-frontend/src/components/layout/SidebarRail.tsx but with
 * the runtime variations (theme CSS vars, pinning, permission gating,
 * hover panels) stubbed for the ecosystem. See the design doc for the
 * deferred list.
 */
export function SidebarRail({
  items,
  moreItems = [],
  onOpenTrackerModal,
  appSwitcher,
  renderPanelContent,
  resolveHref,
  disabled = false,
  panelTitles,
  utility,
  loading = false,
}: SidebarRailProps) {
  // Every slot renders unless the app explicitly turns it off.
  const showAppSwitcher = utility?.appSwitcher !== false;
  const showTracker = utility?.trackerButton !== false;
  const showThemeCustomizer = utility?.themeCustomizer !== false;
  const { isPanelOpen, activePanel, togglePanel, closePanel, isMobileOpen, closeMobile, setRoutePanel } =
    useSidebar();
  const location = useLocation();

  // The below-lg drawer is a way to pick a destination, so it closes once one is picked.
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);
  const navigate = useNavigate();
  const { pins, unpin } = useSidebarRailPins();

  // User-selected rail color ("Customize navigation"). The derived values are
  // injected as --sr-* CSS vars on the rail, clario-frontend useAppTheme parity.
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const { theme } = useSidebarTheme();
  const derived = useMemo(() => deriveSidebarTheme(theme.railColor), [theme.railColor]);
  const dark = isDarkColor(theme.railColor);
  const railVars = {
    '--sr-bg': derived.railBg,
    '--sr-icon': derived.iconColor,
    '--sr-hover': derived.hoverBg,
    '--sr-active': derived.activeBg,
    '--sr-active-icon': derived.activeIconColor,
    '--sr-border': derived.borderColor,
  } as CSSProperties;

  // Re-theme the WHOLE app from the chosen rail color: derive a full 11-shade
  // palette and write it onto `:root` so every `--indigo-*`, `--primary`,
  // `--primary-light` and `--accent` token (and therefore every `bg-primary`
  // button, `ring`/`ring-ring` highlight and `indigo-*` selected state)
  // resolves to the user's color. Mirrors clario-frontend's useAppTheme and the
  // CRM app's `useAppTheme` hook — the shell only applied the rail-local
  // `--sr-*` vars before, so buttons/highlights never followed the theme.
  useEffect(() => {
    const root = document.documentElement;
    const palette = derivePalette(theme.railColor);

    for (const shade of PALETTE_SHADES) {
      root.style.setProperty(`--indigo-${shade}`, palette[shade]);
    }
    root.style.setProperty('--primary', palette['500']);
    root.style.setProperty('--primary-light', palette['50']);
    root.style.setProperty('--accent', palette['100']);

    return () => {
      for (const shade of PALETTE_SHADES) {
        root.style.setProperty(`--indigo-${shade}`, DEFAULT_PALETTE[shade]);
      }
      root.style.setProperty('--primary', DEFAULT_PALETTE['500']);
      root.style.setProperty('--primary-light', DEFAULT_PALETTE['50']);
      root.style.setProperty('--accent', DEFAULT_PALETTE['100']);
    };
  }, [theme.railColor]);

  // Floating hover panel state (separate from the pinned panel).
  const [hoverPanel, setHoverPanel] = useState<string | null>(null);
  const [hoverAnchor, setHoverAnchor] = useState<DOMRect | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHoverHide = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);
  const cancelHoverOpen = useCallback(() => {
    if (hoverOpenTimer.current) {
      clearTimeout(hoverOpenTimer.current);
      hoverOpenTimer.current = null;
    }
  }, []);
  const scheduleHoverHide = useCallback(() => {
    cancelHoverHide();
    hoverTimer.current = setTimeout(() => setHoverPanel(null), 180);
  }, [cancelHoverHide]);
  const handleHoverEnter = useCallback(
    (panelId: string, el: HTMLElement) => {
      cancelHoverHide();
      cancelHoverOpen();
      // Don't show a hover preview for the panel that's already pinned open.
      if (isPanelOpen && activePanel === panelId) return;
      const open = () => {
        setHoverAnchor(el.getBoundingClientRect());
        setHoverPanel(panelId);
      };
      // Only the first open waits; once a panel is showing, moving between icons switches at once.
      if (hoverPanel) {
        open();
        return;
      }
      hoverOpenTimer.current = setTimeout(() => {
        hoverOpenTimer.current = null;
        if (el.isConnected) open();
      }, HOVER_OPEN_DELAY_MS);
    },
    [cancelHoverHide, cancelHoverOpen, isPanelOpen, activePanel, hoverPanel],
  );

  useEffect(
    () => () => {
      cancelHoverHide();
      cancelHoverOpen();
    },
    [cancelHoverHide, cancelHoverOpen],
  );

  const email = useAuthStore((s) => s.user?.email ?? '');
  const initials =
    (email.split('@')[0] ?? '')
      .split(/[._-]+/)
      .map((p) => p[0])
      .filter(Boolean)
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'U';

  const profileActive =
    location.pathname === '/profile' || location.pathname.startsWith('/profile/');

  // Highlight only the single best (longest-prefix) match so a root item like
  // Home (/admin) doesn't stay lit on every nested route (/admin/configuration).
  // An item matches on its `href` OR any of its `matchPaths` (pages that live
  // under a different path but belong to this item, e.g. Settings owns
  // /workspace/permissions). The longest matched path wins so a more-specific
  // item beats the app-root Home item on a deep link / full page reload.
  const activeId = useMemo(() => {
    const prefixMatches = (path: string) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`);
    const bestMatchLength = (it: RailItem) => {
      // App-root items (Home) opt into exact matching so they don't stay lit on
      // every deeper page that no other rail item explicitly claims — e.g.
      // `/workspace/masters`, which belongs to the Settings panel, not Home.
      const matchPath = it.exact ? (path: string) => location.pathname === path : prefixMatches;
      const matched = [it.href, ...(it.matchPaths ?? [])].filter(matchPath);
      if (matched.length === 0) return -1;
      return matched.reduce((max, p) => Math.max(max, p.length), 0);
    };
    const candidates = [...items, ...moreItems]
      .map((it) => ({ it, len: bestMatchLength(it) }))
      .filter((c) => c.len >= 0);
    if (candidates.length === 0) return null;
    return candidates.reduce((best, c) => (c.len > best.len ? c : best)).it.id;
  }, [items, moreItems, location.pathname]);

  const routePanel = useMemo(() => {
    const item = [...items, ...moreItems].find((it) => it.id === activeId);
    return item?.panelId ?? null;
  }, [items, moreItems, activeId]);
  // The TopBar collapse control reads this, so a page with a side detail keeps
  // the control after the panel is closed.
  useEffect(() => {
    setRoutePanel(routePanel);
  }, [routePanel, setRoutePanel]);

  const isItemActive = useCallback((item: RailItem) => item.id === activeId, [activeId]);

  const handleClick = useCallback(
    (item: RailItem) => {
      cancelHoverOpen();
      setHoverPanel(null);
      // clario-frontend parity: panel items toggle the pinned panel and only
      // navigate when the panel is opening; plain items just navigate.
      if (item.panelId) {
        const willOpen = !(isPanelOpen && activePanel === item.panelId);
        togglePanel(item.panelId);
        const href = resolveHref?.(item) ?? item.href;
        if (href && willOpen) navigate(href);
      } else {
        closePanel({ forget: true });
        if (item.openInNewTab) {
          window.open(item.href, '_blank', 'noopener,noreferrer');
        } else {
          navigate(item.href);
        }
      }
    },
    [navigate, closePanel, togglePanel, isPanelOpen, activePanel, resolveHref, cancelHoverOpen],
  );

  // Item colors come from the --sr-* theme vars (clario-frontend parity), so
  // the rail honors the user's "Customize navigation" color. Inline styles are
  // required here: the values are computed from runtime data.
  const renderRailItem = (item: RailItem) => (
    <RailItemView
      key={item.id}
      item={item}
      active={isItemActive(item)}
      dark={dark}
      onClick={() => handleClick(item)}
      onHoverEnter={(anchor) => {
        if (item.panelId) handleHoverEnter(item.panelId, anchor);
      }}
      onHoverLeave={() => {
        if (!item.panelId) return;
        cancelHoverOpen();
        scheduleHoverHide();
      }}
    />
  );

  // More items pinned onto the rail — order follows the user's pin order
  // (clario-frontend parity: right-click to unpin).
  const pinnedMoreItems = pins
    .map((id) => moreItems.find((m) => m.id === id))
    .filter((m): m is RailItem => Boolean(m));

  const renderPinnedRailItem = (item: RailItem) => (
    <ContextMenu key={item.id}>
      <ContextMenuTrigger asChild>
        <div>{renderRailItem(item)}</div>
      </ContextMenuTrigger>
      <ContextMenuContent className="min-w-[9rem] p-0.5">
        <ContextMenuItem
          onClick={() => unpin(item.id)}
          className="text-md gap-1.5 px-2 py-1 [&_svg]:!size-3"
        >
          <PinOff className="text-gray-500" strokeWidth={1.5} />
          <span>Unpin from sidebar</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );

  // Items shown inside the More popover (already permission-filtered by the
  // shell), plus the always-present "Customize navigation" footer action
  // (clario-frontend parity — opens the sidebar theme dialog).
  const moreItemsForPopover: SidebarMoreItem[] = [
    ...moreItems.map<SidebarMoreItem>((m) => ({
      id: m.id,
      label: m.label,
      icon: m.icon,
      href: m.href,
      openInNewTab: m.openInNewTab,
      isVisible: true,
      tileColor: m.tileColor ?? 'gray',
      panelId: m.panelId,
    })),
    ...(showThemeCustomizer
      ? [
          {
            id: 'customize-navigation',
            label: 'Customize navigation',
            icon: Palette,
            isVisible: true,
            pinnable: false,
            tileColor: 'purple' as const,
            onSelect: () => setThemeDialogOpen(true),
          },
        ]
      : []),
  ];

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        id="sidebar-rail"
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen w-16 flex-col items-center border-r py-2.5 transition-[transform,visibility] duration-200 motion-reduce:transition-none',
          // Below lg there is no room beside the content, so the rail is an off-canvas
          // drawer under the TopBar, whose menu trigger opens and closes it.
          'max-lg:top-12 max-lg:h-[calc(100dvh-3rem)]',
          !isMobileOpen && 'max-lg:invisible max-lg:-translate-x-full',
        )}
        style={{
          ...railVars,
          background: 'var(--sr-bg)',
          borderColor: 'var(--sr-border)',
        }}
      >
        {/* `inert` (not just pointer-events-none) so the items are also out of
            the tab order and hidden from assistive tech while the app is down —
            a dimmed-but-focusable nav would still let a keyboard user activate
            a route that cannot load. */}
        <nav
          inert={disabled || undefined}
          aria-disabled={disabled || undefined}
          className={cn(
            'scrollbar-hide mt-1 flex w-full flex-1 flex-col items-center gap-2.5 overflow-y-auto px-1',
            disabled && 'pointer-events-none select-none opacity-40',
          )}
        >
          {loading ? (
            <div data-testid="sidebar-rail-pending" role="status" aria-label="Loading navigation" />
          ) : (
            <div className="flex w-full flex-col items-center gap-2.5">
              {items.map(renderRailItem)}
              {pinnedMoreItems.map(renderPinnedRailItem)}

              {/* The More (9-dot) popover always renders so its entries — including
                  the always-present "Customize navigation" action — live one
                  consistent click behind the grid icon across every app. */}
              <SidebarMorePopover
                items={moreItemsForPopover}
                trigger={
                  <div className="flex flex-col items-center gap-0.5">
                    <button
                      type="button"
                      aria-label="More"
                      className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors"
                      style={{ color: 'var(--sr-icon)', opacity: dark ? 0.65 : 0.6 }}
                    >
                      <Grip className="h-5 w-5" strokeWidth={1.5} />
                    </button>
                    {/* Same caption treatment as a rail item, minus the tooltip —
                        "More" always fits, and this trigger opens a popover. */}
                    <span
                      className="pointer-events-none line-clamp-2 w-full select-none break-words text-center text-xs font-medium leading-tight tracking-[0.01em]"
                      style={{ color: 'var(--sr-icon)', opacity: dark ? 0.65 : 0.6 }}
                    >
                      More
                    </span>
                  </div>
                }
              />
            </div>
          )}
        </nav>

        <div
          className="flex flex-col items-center gap-2.5 border-t pt-2"
          style={{ borderColor: 'var(--sr-border)' }}
        >
          {/* App switcher — grid hover launcher composed by the shell. */}
          {showAppSwitcher && appSwitcher}

          {showTracker && onOpenTrackerModal && (
            <div className="flex flex-col items-center gap-0.5">
              <button
                type="button"
                onClick={onOpenTrackerModal}
                aria-label="Download Tracker"
                className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors"
                style={{ color: 'var(--sr-icon)', opacity: dark ? 0.65 : 0.6 }}
              >
                <Download className="h-5 w-5" strokeWidth={1.5} />
              </button>
              <span
                className="pointer-events-none select-none text-xs font-medium leading-none tracking-[0.01em]"
                style={{ color: 'var(--sr-icon)', opacity: dark ? 0.65 : 0.6 }}
              >
                Tracker
              </span>
            </div>
          )}

          {/* User popover — opens to the right with a Profile link (the
              platform profile page, shared across all apps) and Sign Out. */}
          <div className="mt-1">
            <UserMenu
              side="right"
              align="end"
              trigger={
                <button
                  type="button"
                  aria-label="User menu"
                  aria-current={profileActive ? 'page' : undefined}
                  className={cn(
                    'flex h-[30px] w-[30px] items-center justify-center rounded-full transition-shadow',
                    profileActive
                      ? 'shadow-[0_0_0_2px_rgba(59,130,246,0.45)]'
                      : 'hover:shadow-[0_0_0_2px_rgba(59,130,246,0.15)]',
                  )}
                >
                  <Avatar className="h-[30px] w-[30px]">
                    <AvatarFallback className="bg-blue-50 text-sm font-semibold text-blue-800">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              }
            />
          </div>
        </div>
      </aside>

      {/* Floating hover preview, anchored to the hovered rail icon. */}
      {hoverPanel && hoverAnchor && renderPanelContent && !loading ? (
        <SidebarHoverPanel
          panelId={hoverPanel}
          anchorRect={hoverAnchor}
          items={items}
          // clario-frontend parity: clicking a link inside the hover panel
          // closes the hover card AND pins the panel open (it sticks).
          body={renderPanelContent(hoverPanel, () => {
            const panelId = hoverPanel;
            setHoverPanel(null);
            togglePanel(panelId);
          })}
          onMouseEnter={cancelHoverHide}
          onMouseLeave={scheduleHoverHide}
          onCollapse={() => setHoverPanel(null)}
          panelTitles={panelTitles}
        />
      ) : null}

      {/* Theme customizer dialog — opened from the More popover. */}
      {showThemeCustomizer && (
        <SidebarThemePanel open={themeDialogOpen} onOpenChange={setThemeDialogOpen} />
      )}
    </TooltipProvider>
  );
}
